import { InjectionToken } from '@angular/core';
import { BridgeSource, EDITOR_BRIDGE_VERSION } from './editor-bridge.protocol';

export type BridgeWindow = Window & typeof globalThis;

/** The window the bridge listens on and opens from; replaced in tests. */
export const BRIDGE_WINDOW = new InjectionToken<BridgeWindow>('BridgeWindow', {
  providedIn: 'root',
  factory: (): BridgeWindow => window,
});

/** The other side of the bridge: an iframe's window, or the parent window. */
export interface BridgePeer {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface BridgeMessageTarget {
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
}

export interface BridgeEndpointOptions<In> {
  listenOn: BridgeMessageTarget;
  peer: () => BridgePeer | null;
  /** Exact origin of the peer, e.g. `https://editor.infinitform.com`. */
  peerOrigin: string;
  ownSource: BridgeSource;
  parse: (data: unknown) => In | null;
}

/** The origin of an http(s) address, or null. */
export function toOrigin(address: string | null | undefined): string | null {
  if (!address) return null;
  try {
    const url = new URL(address);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.origin : null;
  } catch {
    return null;
  }
}

/**
 * One end of the cabinet-editor bridge. Accepts only messages that come from
 * the peer window at the peer origin and parse, and posts only to that origin.
 */
export class BridgeEndpoint<In extends { type: string; payload: unknown }, Out extends { type: string; payload: unknown }> {
  private listener: ((event: MessageEvent) => void) | null = null;

  constructor(private readonly options: BridgeEndpointOptions<In>) {
    if (toOrigin(options.peerOrigin) !== options.peerOrigin) {
      throw new Error(`The bridge needs an exact peer origin, got "${options.peerOrigin}"`);
    }
  }

  start(onMessage: (message: In) => void): void {
    this.stop();
    this.listener = (event: MessageEvent): void => {
      if (event.origin !== this.options.peerOrigin) return;
      const peer = this.options.peer();
      if (!peer || (event.source as unknown) !== peer) return;
      const message = this.options.parse(event.data);
      if (message) onMessage(message);
    };
    this.options.listenOn.addEventListener('message', this.listener);
  }

  send(message: Out): void {
    const peer = this.options.peer();
    if (!peer) return;
    peer.postMessage(
      { source: this.options.ownSource, v: EDITOR_BRIDGE_VERSION, type: message.type, payload: message.payload },
      this.options.peerOrigin
    );
  }

  stop(): void {
    if (!this.listener) return;
    this.options.listenOn.removeEventListener('message', this.listener);
    this.listener = null;
  }
}
